import React, { useLayoutEffect, useMemo } from 'react';
import { usePageTopBarOptional } from '@/components/layout/PageTopBarContext';
import {
  PageBreadcrumbs,
  type BreadcrumbSegment,
} from '@/components/PageBreadcrumbs';
import { cn } from '@/lib/utils';
import { DataFreshness } from '@/components/DataFreshness';
import { InfoTooltip } from '@/components/InfoTooltip';
import type { DataSourceId } from '@shared/data-sources';

interface PageHeaderProps {
  title?: React.ReactNode;
  /**
   * Visible text under the header. Only for what the page shows right now; put what the page is
   * for in `info` (ui-components.mdc, "Keep explanatory text off the page").
   */
  description?: React.ReactNode;
  /**
   * What the page is for, in a tooltip on an info icon beside the page's title (the last crumb in
   * the top bar), as PageSection's `info` does for a section. Warnings, empty states and status stay
   * visible on the page.
   */
  info?: React.ReactNode;
  /** Names what `info` explains, for screen readers. Defaults to the page's title when it is text. */
  infoLabel?: string;
  /**
   * Page-level actions (Export, Refresh, Add, etc.).
   * Rendered top-right in the page body — never the sticky AppTopBar.
   */
  actions?: React.ReactNode;
  /** Same row as `actions`, aligned left (e.g. Back). */
  leadingActions?: React.ReactNode;
  /** Structured breadcrumb trail shown in the sticky top bar. */
  breadcrumbItems?: BreadcrumbSegment[];
  /** Custom breadcrumb markup for the sticky top bar. */
  breadcrumbs?: React.ReactNode;
  /**
   * The record this page shows ("Order WO-0012", a brand's name). It becomes the last crumb, after
   * `breadcrumbItems`, and goes in front of the browser tab's title (C23).
   */
  recordTitle?: string;
  /**
   * The synced data sources the page shows. Adds the DataFreshness chip ("Updated 3h ago") before
   * the actions (data-freshness.mdc).
   */
  sources?: readonly DataSourceId[];
  className?: string;
}

/**
 * Registers the breadcrumb trail in the sticky top bar.
 * Description and page actions render in the page body (not the top bar).
 * On public pages (no top bar), falls back to an inline title + actions block.
 */
export function PageHeader({
  title,
  description,
  info,
  infoLabel,
  actions,
  leadingActions,
  breadcrumbItems,
  breadcrumbs,
  recordTitle,
  sources,
  className,
}: PageHeaderProps) {
  const topBar = usePageTopBarOptional();
  const setPageTopBar = topBar?.setPageTopBar;

  const topBarBreadcrumbs = useMemo(() => {
    if (breadcrumbs) {
      if (!info) return breadcrumbs;
      return (
        <div className="flex min-w-0 items-center gap-1.5">
          {breadcrumbs}
          <InfoTooltip label={infoLabel ?? (typeof title === 'string' ? title : undefined)}>
            {info}
          </InfoTooltip>
        </div>
      );
    }
    const items = breadcrumbItems ?? (title ? [{ label: title }] : []);
    const all = recordTitle ? [...items, { label: recordTitle }] : items;
    return all.length ? <PageBreadcrumbs items={all} info={info} infoLabel={infoLabel} /> : null;
  }, [breadcrumbs, breadcrumbItems, title, recordTitle, info, infoLabel]);

  useLayoutEffect(() => {
    if (!setPageTopBar) return undefined;
    setPageTopBar({ breadcrumbs: topBarBreadcrumbs, recordTitle: recordTitle ?? null });
    return () => {
      setPageTopBar({ breadcrumbs: null, recordTitle: null });
    };
  }, [setPageTopBar, topBarBreadcrumbs, recordTitle]);

  const freshness = sources?.length ? <DataFreshness sources={sources} /> : null;
  const actionsWithFreshness =
    freshness || actions ? (
      <>
        {freshness}
        {actions}
      </>
    ) : null;

  if (topBar) {
    if (!description && !actionsWithFreshness && !leadingActions) return null;

    if (!description) {
      return (
        <div className={cn('flex flex-wrap items-center gap-2', className)}>
          {leadingActions}
          {actionsWithFreshness ? (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {actionsWithFreshness}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6',
          className
        )}
      >
        <div className="min-w-0 flex-1 space-y-3">
          {leadingActions ? (
            <div className="flex flex-wrap items-center gap-2">{leadingActions}</div>
          ) : null}
          <div className="text-sm text-muted-foreground">{description}</div>
        </div>
        {actionsWithFreshness ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {actionsWithFreshness}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn('mb-6 flex flex-col gap-4', className)}>
      {topBarBreadcrumbs ? <div className="text-sm">{topBarBreadcrumbs}</div> : null}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="min-w-0 flex-1 space-y-1">
          {leadingActions ? (
            <div className="flex flex-wrap items-center gap-2">{leadingActions}</div>
          ) : null}
          {title ? (
            <h1
              className={cn(
                'text-2xl font-semibold tracking-tight text-foreground',
                info && 'flex items-center gap-1.5'
              )}
            >
              {title}
              {info ? (
                <InfoTooltip label={infoLabel ?? (typeof title === 'string' ? title : undefined)}>
                  {info}
                </InfoTooltip>
              ) : null}
            </h1>
          ) : null}
          {description ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
        </div>
        {actionsWithFreshness ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {actionsWithFreshness}
          </div>
        ) : null}
      </div>
    </div>
  );
}
